import React, { useState, useEffect } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { useStore } from "../store";
import { fmeaStatusStyle, rpnColor, FMEA_PURPLE } from "../data/mockData";
import { useFmeas, useIncidents, usePatchFmea } from "../api/sitroom";
import { generateFmeaAction, scoreFmeaRpn } from "../api/fmea";
import { RoleGuard } from "./RoleGuard";

const SectionTitle = ({ children }) => (
  <div
    className="mt-5 mb-2.5"
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 10,
      letterSpacing: "1.6px",
      color: "#c89adb",
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div
    className="rounded-md p-2.5"
    style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}
  >
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        letterSpacing: "1.2px",
        color: "#9198b0",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div
      className="mt-1"
      style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#f0f2f8" }}
    >
      {value}
    </div>
  </div>
);

const editableStyle = {
  background: "#0d0f18",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#f0f2f8",
  fontFamily: "'DM Mono', monospace",
  fontSize: 11,
  width: "100%",
  borderRadius: 4,
  padding: "8px 10px",
};

const NumberInput = ({ label, value, onChange, testId }) => (
  <div className="flex-1">
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        letterSpacing: "1.2px",
        color: "#9198b0",
        textTransform: "uppercase",
        textAlign: "center",
      }}
    >
      {label}
    </div>
    <input
      type="number"
      min={1}
      max={10}
      value={value}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        if (Number.isNaN(v)) return;
        onChange(Math.max(1, Math.min(10, v)));
      }}
      className="mt-1.5 text-center"
      style={{
        ...editableStyle,
        fontSize: 18,
        fontWeight: 500,
        padding: "8px 6px",
      }}
      data-testid={testId}
    />
  </div>
);

export const FmeaPanel = () => {
  const isOpen = useStore((s) => s.isFmeaPanelOpen);
  const closeFmeaPanel = useStore((s) => s.closeFmeaPanel);
  const selectedFmeaId = useStore((s) => s.selectedFmeaId);
  const openPanel = useStore((s) => s.openPanel);
  const openIntegrationModal = useStore((s) => s.openIntegrationModal);
  const addToast = useStore((s) => s.addToast);

  const { data: list = [] } = useFmeas();
  const { data: incidents = [] } = useIncidents();
  const patchMutation = usePatchFmea();
  const updateFmea = (id, body) => patchMutation.mutate({ id, patch: body });

  const current = list.find((f) => f.id === selectedFmeaId);

  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [scoringRpn, setScoringRpn] = useState(false);
  const [rpnAi, setRpnAi] = useState(null); // { sev_reason, occ_reason, det_reason, accepted }

  useEffect(() => {
    if (current) setDraft({ ...current });
    else setDraft(null);
    setGenerating(false);
    setRpnAi(null);
  }, [current]);

  if (!isOpen || !current || !draft) return null;

  const rpn = draft.severity * draft.occurrence * draft.detection;
  const rpnC = rpnColor(rpn);
  const st = fmeaStatusStyle(draft.status);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    const incident = incidents.find((i) => i.id === draft.sourceIncident);
    try {
      const data = await generateFmeaAction({
        failureMode: draft.failureMode,
        rootCause: draft.cause,
        impact: draft.effect,
        linkedParties: incident
          ? incident.linkedParties.map((p) => p.name).join(", ")
          : "Unknown",
        severity: draft.severity >= 9 ? "S1" : draft.severity >= 7 ? "S2" : draft.severity >= 4 ? "S3" : "S4",
        sourceIncident: draft.sourceIncident,
      });
      setDraft((d) => ({ ...d, recommendedAction: data.action, aiGenerated: true }));
      updateFmea(draft.id, { recommendedAction: data.action, aiGenerated: true });
      addToast({
        type: "info",
        title: "AI action generated",
        body: `Claude Sonnet drafted a new recommended action for ${draft.id}`,
      });
    } catch (err) {
      addToast({
        type: "s1",
        title: "AI generation failed",
        body: "Kept current text — try again in a moment",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = () => {
    updateFmea(draft.id, draft);
    addToast({ type: "info", title: "Changes saved", body: draft.id });
  };
  const handleApprove = () => {
    updateFmea(draft.id, { ...draft, status: "approved", reviewedBy: "G. Castro" });
    addToast({ type: "info", title: "FMEA approved", body: `${draft.id} added to FMEA registry` });
    closeFmeaPanel();
  };
  const handleReject = () => {
    updateFmea(draft.id, { ...draft, status: "rejected", reviewedBy: "G. Castro" });
    addToast({ type: "info", title: "FMEA rejected", body: draft.id });
    closeFmeaPanel();
  };

  // === AI-suggest RPN ===
  const handleScoreRpn = async () => {
    if (scoringRpn) return;
    setScoringRpn(true);
    const incident = incidents.find((i) => i.id === draft.sourceIncident);
    try {
      const data = await scoreFmeaRpn({
        failureMode: draft.failureMode,
        failureEffect: draft.effect,
        potentialCause: draft.cause,
        currentControls: draft.currentControls,
        incidentSeverity: incident ? incident.severity.toUpperCase() : "S2",
        productionImpact: draft.effect,
      });
      setDraft((d) => ({ ...d, severity: data.sev, occurrence: data.occ, detection: data.det, aiScored: true }));
      setRpnAi({
        sev_reason: data.sev_reason,
        occ_reason: data.occ_reason,
        det_reason: data.det_reason,
        accepted: false,
      });
      addToast({ type: "info", title: "AI scored RPN", body: `Claude Sonnet suggested S${data.sev}·O${data.occ}·D${data.det}` });
    } catch (err) {
      addToast({ type: "s1", title: "AI RPN scoring failed", body: "Kept current values — try again" });
    } finally {
      setScoringRpn(false);
    }
  };

  const handleAcceptRpn = () => {
    setRpnAi((r) => (r ? { ...r, accepted: true } : r));
    updateFmea(draft.id, { severity: draft.severity, occurrence: draft.occurrence, detection: draft.detection, aiScored: true });
    addToast({ type: "info", title: "AI RPN accepted", body: draft.id });
  };
  const handleManualOverride = () => {
    setRpnAi(null);
    setDraft((d) => ({ ...d, aiScored: false }));
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[400] backdrop-enter"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
        onClick={closeFmeaPanel}
        data-testid="fmea-panel-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-[401] panel-enter flex flex-col"
        style={{
          width: "min(560px, 100vw)",
          background: "#0d0f18",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
        }}
        data-testid="fmea-panel"
      >
        <header
          className="relative p-5 pb-3"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}
        >
          <button
            onClick={closeFmeaPanel}
            className="absolute right-4 top-4 flex items-center justify-center rounded-md hover:bg-white/5"
            style={{
              width: 28,
              height: 28,
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9aa1b3",
            }}
            data-testid="close-fmea-panel-btn"
            aria-label="Close FMEA panel"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c89adb" }}>
              {draft.id}
            </span>
            <span
              style={{
                background: "rgba(142,68,173,0.18)",
                border: "1px solid rgba(142,68,173,0.5)",
                color: "#c89adb",
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 3,
                letterSpacing: "1px",
                fontWeight: 500,
              }}
            >
              FMEA
            </span>
            <span
              style={{
                background: st.bg,
                border: `1px solid ${st.border}`,
                color: st.color,
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 3,
                letterSpacing: "1px",
              }}
            >
              {st.label}
            </span>
          </div>
          <div
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 16,
              color: "#f0f2f8",
              paddingRight: 40,
              lineHeight: 1.35,
            }}
          >
            {draft.failureMode}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5 pt-3">
          <SectionTitle>Source Incident</SectionTitle>
          <button
            onClick={() => {
              closeFmeaPanel();
              setTimeout(() => openPanel(draft.sourceIncident), 250);
            }}
            className="w-full text-left rounded-md p-3"
            style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}
            data-testid="fmea-open-source-incident"
          >
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9ec3ec",
                letterSpacing: "0.5px",
              }}
            >
              {draft.sourceIncident} · CLOSED
            </div>
            <div
              className="mt-1"
              style={{
                fontFamily: "'Syne', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                color: "#f0f2f8",
              }}
            >
              {draft.failureMode}
            </div>
            <div
              className="mt-1"
              style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}
            >
              Root cause: {draft.cause}
            </div>
          </button>

          <SectionTitle>FMEA Fields</SectionTitle>
          <div className="space-y-3">
            <div>
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Failure Mode
              </div>
              <input
                style={editableStyle}
                value={draft.failureMode}
                onChange={(e) => setField("failureMode", e.target.value)}
                data-testid="fmea-failure-mode-input"
              />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Effect
              </div>
              <input
                style={editableStyle}
                value={draft.effect}
                onChange={(e) => setField("effect", e.target.value)}
              />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Cause
              </div>
              <input
                style={editableStyle}
                value={draft.cause}
                onChange={(e) => setField("cause", e.target.value)}
              />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "1.4px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Current Controls
              </div>
              <input
                style={editableStyle}
                value={draft.currentControls}
                onChange={(e) => setField("currentControls", e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    letterSpacing: "1.4px",
                    color: "#9198b0",
                    textTransform: "uppercase",
                  }}
                >
                  Recommended Action
                  {draft.aiGenerated && !generating && (
                    <span
                      className="ml-2"
                      style={{
                        color: "#c89adb",
                        background: "rgba(142,68,173,0.12)",
                        border: "1px solid rgba(142,68,173,0.35)",
                        padding: "1px 6px",
                        borderRadius: 3,
                        fontSize: 8,
                        letterSpacing: "1px",
                      }}
                    >
                      AI · CLAUDE SONNET
                    </span>
                  )}
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md"
                  style={{
                    background: generating ? "rgba(142,68,173,0.05)" : "rgba(142,68,173,0.14)",
                    border: "1px solid rgba(142,68,173,0.45)",
                    color: "#c89adb",
                    fontFamily: "'Syne', system-ui, sans-serif",
                    fontWeight: 600,
                    fontSize: 10,
                    cursor: generating ? "wait" : "pointer",
                    opacity: generating ? 0.7 : 1,
                  }}
                  data-testid="fmea-generate-ai-btn"
                >
                  {generating ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Sparkles size={11} />
                  )}
                  {generating ? "Generating…" : "Generate with AI"}
                </button>
              </div>
              <div className="relative">
                <textarea
                  rows={4}
                  style={{
                    ...editableStyle,
                    resize: "vertical",
                    color: generating ? "#9198b0" : "#f0f2f8",
                    fontStyle: generating ? "italic" : "normal",
                  }}
                  value={generating ? "Claude Sonnet is drafting a recommended action…" : draft.recommendedAction}
                  onChange={(e) => setField("recommendedAction", e.target.value)}
                  disabled={generating}
                  data-testid="fmea-action-input"
                />
                {generating && (
                  <div
                    className="absolute inset-0 flex items-center justify-center rounded-md pointer-events-none"
                    style={{ background: "rgba(15,17,23,0.5)" }}
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" color="#c89adb" />
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c89adb", letterSpacing: "1px" }}>
                        GENERATING WITH CLAUDE SONNET…
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <SectionTitle>RPN Calculator</SectionTitle>
          <div
            className="rounded-md p-4"
            style={{
              background: "#0d0f18",
              border: `1px solid ${rpnC}33`,
            }}
          >
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                {draft.aiScored && (
                  <span
                    style={{
                      background: "rgba(24,95,165,0.18)",
                      border: "1px solid rgba(24,95,165,0.5)",
                      color: "#9ec3ec",
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      padding: "2px 8px",
                      borderRadius: 3,
                      letterSpacing: "1px",
                      fontWeight: 500,
                    }}
                    data-testid="fmea-ai-scored-badge"
                  >
                    AI · CLAUDE SONNET
                  </span>
                )}
                {rpnAi && !rpnAi.accepted && (
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      color: "#f3b14a",
                      letterSpacing: "0.5px",
                    }}
                  >
                    REVIEW · NOT YET ACCEPTED
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleScoreRpn}
                  disabled={scoringRpn}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md"
                  style={{
                    background: scoringRpn ? "rgba(24,95,165,0.06)" : "rgba(24,95,165,0.16)",
                    border: "1px solid rgba(24,95,165,0.55)",
                    color: "#9ec3ec",
                    fontFamily: "'Syne', system-ui, sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    cursor: scoringRpn ? "wait" : "pointer",
                    opacity: scoringRpn ? 0.7 : 1,
                  }}
                  data-testid="fmea-score-rpn-btn"
                >
                  {scoringRpn ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {scoringRpn ? "Scoring…" : rpnAi ? "Regenerate" : "AI Score RPN"}
                </button>
                {rpnAi && !rpnAi.accepted && (
                  <button
                    onClick={handleAcceptRpn}
                    className="px-2.5 py-1.5 rounded-md"
                    style={{
                      background: "rgba(39,174,96,0.14)",
                      border: "1px solid rgba(39,174,96,0.5)",
                      color: "#4ed084",
                      fontFamily: "'Syne', system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: 11,
                    }}
                    data-testid="fmea-accept-rpn-btn"
                  >
                    Accept
                  </button>
                )}
                {rpnAi && (
                  <button
                    onClick={handleManualOverride}
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 10,
                      color: "#9198b0",
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                    }}
                    data-testid="fmea-manual-override-btn"
                  >
                    Manual Override
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <NumberInput
                  label="Severity"
                  value={draft.severity}
                  onChange={(v) => setField("severity", v)}
                  testId="fmea-severity-input"
                />
                {rpnAi && !rpnAi.accepted && rpnAi.sev_reason && (
                  <div
                    className="mt-1.5"
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 10,
                      color: "#9198b0",
                      fontStyle: "italic",
                      lineHeight: 1.4,
                    }}
                    data-testid="fmea-sev-reason"
                  >
                    {rpnAi.sev_reason}
                  </div>
                )}
              </div>
              <div style={{ color: "#9198b0", fontSize: 18, paddingBottom: 8 }}>×</div>
              <div className="flex-1">
                <NumberInput
                  label="Occurrence"
                  value={draft.occurrence}
                  onChange={(v) => setField("occurrence", v)}
                  testId="fmea-occurrence-input"
                />
                {rpnAi && !rpnAi.accepted && rpnAi.occ_reason && (
                  <div
                    className="mt-1.5"
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 10,
                      color: "#9198b0",
                      fontStyle: "italic",
                      lineHeight: 1.4,
                    }}
                    data-testid="fmea-occ-reason"
                  >
                    {rpnAi.occ_reason}
                  </div>
                )}
              </div>
              <div style={{ color: "#9198b0", fontSize: 18, paddingBottom: 8 }}>×</div>
              <div className="flex-1">
                <NumberInput
                  label="Detection"
                  value={draft.detection}
                  onChange={(v) => setField("detection", v)}
                  testId="fmea-detection-input"
                />
                {rpnAi && !rpnAi.accepted && rpnAi.det_reason && (
                  <div
                    className="mt-1.5"
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 10,
                      color: "#9198b0",
                      fontStyle: "italic",
                      lineHeight: 1.4,
                    }}
                    data-testid="fmea-det-reason"
                  >
                    {rpnAi.det_reason}
                  </div>
                )}
              </div>
              <div
                className="text-center rounded-md"
                style={{
                  background: `${rpnC}15`,
                  border: `1px solid ${rpnC}80`,
                  padding: "8px 16px",
                  minWidth: 100,
                }}
              >
                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    letterSpacing: "1.5px",
                    color: "#9198b0",
                  }}
                >
                  RPN
                </div>
                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 32,
                    color: rpnC,
                    fontWeight: 500,
                    lineHeight: 1,
                    marginTop: 2,
                  }}
                  data-testid="fmea-rpn-live"
                >
                  {rpn}
                </div>
              </div>
            </div>
            <div
              className="mt-3 text-center"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: rpnC,
                letterSpacing: "1px",
              }}
            >
              {rpn >= 200 ? "HIGH PRIORITY" : rpn >= 100 ? "MEDIUM PRIORITY" : "LOW PRIORITY"}
            </div>
          </div>

          <SectionTitle>Audit Trail</SectionTitle>
          <Field label="Generated By" value={draft.generatedBy === "auto" ? "System (auto)" : draft.generatedBy} />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Field label="Generated At" value={draft.generatedAt} />
            <Field label="Reviewed By" value={draft.reviewedBy || "—"} />
          </div>
        </div>

        <footer
          className="flex items-center gap-2 p-3 flex-wrap"
          style={{ background: "#0d0f18", borderTop: "1px solid rgba(255,255,255,0.12)" }}
        >
          {draft.status === "approved" && (
            <>
              <button
                onClick={() => useStore.getState().openIntegrationModal("jira", draft.id)}
                className="px-3 py-2 rounded-md flex items-center gap-1.5"
                style={{
                  background: "rgba(0,82,204,0.14)",
                  border: "1px solid rgba(0,82,204,0.5)",
                  color: "#5fa3ff",
                  fontFamily: "'Syne', system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                }}
                data-testid="fmea-create-jira-btn"
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    background: "#0052CC",
                    color: "#fff",
                    borderRadius: 3,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    textAlign: "center",
                    lineHeight: "14px",
                  }}
                >
                  J
                </span>
                Create Jira ticket
              </button>
              <button
                onClick={() => useStore.getState().openIntegrationModal("planner", draft.id)}
                className="px-3 py-2 rounded-md flex items-center gap-1.5"
                style={{
                  background: "rgba(123,94,167,0.14)",
                  border: "1px solid rgba(123,94,167,0.5)",
                  color: "#bda3d8",
                  fontFamily: "'Syne', system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                }}
                data-testid="fmea-create-planner-btn"
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    background: "#7B5EA7",
                    color: "#fff",
                    borderRadius: 3,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    textAlign: "center",
                    lineHeight: "14px",
                  }}
                >
                  P
                </span>
                Add to MS Planner
              </button>
            </>
          )}
          {draft.status !== "approved" && (
            <button
              onClick={handleReject}
              className="px-3 py-2 rounded-md"
              style={{
                background: "rgba(226,75,74,0.10)",
                border: "1px solid rgba(226,75,74,0.5)",
                color: "#ff7e7c",
                fontFamily: "'Syne', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 11,
              }}
              data-testid="fmea-panel-reject-btn"
            >
              ✗ Reject
            </button>
          )}
          <button
            onClick={handleSave}
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#cfd1d8",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: 11,
            }}
            data-testid="fmea-panel-save-btn"
          >
            Save Changes
          </button>
          <div className="flex-1" />
          {draft.status !== "approved" && (
            <RoleGuard
              minRole="escalation_manager"
              fallback={
                <span
                  className="px-3 py-2 rounded-md"
                  style={{
                    background: "rgba(243,177,74,0.08)",
                    border: "1px solid rgba(243,177,74,0.35)",
                    color: "#f3b14a",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.6px",
                  }}
                  data-testid="fmea-panel-approve-pending"
                >
                  Pending manager approval
                </span>
              }
            >
              <button
                onClick={handleApprove}
                className="px-3 py-2 rounded-md"
                style={{
                  background: "rgba(39,174,96,0.14)",
                  border: `1px solid rgba(39,174,96,0.5)`,
                  color: "#4ed084",
                  fontFamily: "'Syne', system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.4px",
                }}
                data-testid="fmea-panel-approve-btn"
              >
                ✓ Approve & Add to FMEA
              </button>
            </RoleGuard>
          )}
        </footer>
      </aside>
    </>
  );
};
